import React, { useEffect, useMemo, useState } from 'react';
import './Charity.scss';
import Header from '../Header';
import Footer from '../Footer/Footer';
import TelegramPopup from '../../Common/TelegramPopup/TelegramPopup';
import {
  FaHeart,
  FaHandHoldingHeart,
  FaUsers,
  FaShieldAlt,
  FaCalendarAlt,
  FaGift,
  FaArrowRight,
} from 'react-icons/fa';
import { BASE_BUY_URL, BASE_CHART_URL } from '../../../config/siteContent';
import {
  ASTROLESCENT_API_URL,
  OWNER_TREASURY_SNAPSHOT,
  OCI_ENDOWMENT_FALLBACK_SNAPSHOT,
  buildEndowmentTotals,
  calculateEndowmentTotalValueUSD,
  fetchOciEndowmentPoolSnapshot,
} from '../TreasuryPage/treasuryValue';

const formatCurrency = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '$0.00';
  }
  return `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatTokenAmount = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '0';
  }
  if (value >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
  if (value >= 1e3) return `${(value / 1e3).toFixed(1)}K`;
  return value.toLocaleString();
};

const ENDOWMENT_SEED_DCKS = OWNER_TREASURY_SNAPSHOT.endowmentSeedDcks;

const charityDonationsData = [
  {
    date: '2025-09-11',
    organization: 'Saving Innocence',
    amount: 5875.2,
    description: 'Support for anti-trafficking and survivor-focused recovery work.',
    focus: 'Human Trafficking',
    status: 'DONE',
    referenceText: 'Community update posted by REDDICKS on X (September 11, 2025).',
  },
  {
    date: '2026-02-19',
    organization: 'Xian Development',
    amount: 1750,
    description: 'Ecosystem development contribution for Xian.',
    focus: 'Ecosystem Development',
    status: 'DONE',
    referenceText: 'Team-confirmed donation (February 19, 2026).',
  },
  {
    date: '2026-04-02',
    organization: 'Xian Development',
    amount: 540,
    description: 'Additional ecosystem development contribution while the endowment structure was being separated out.',
    focus: 'Ecosystem Development',
    status: 'DONE',
    referenceText: 'Team-confirmed donation (April 2, 2026).',
  },
];

const Charity = () => {
  const [ociPoolSnapshot, setOciPoolSnapshot] = useState(OCI_ENDOWMENT_FALLBACK_SNAPSHOT);
  const [endowmentValueUSD, setEndowmentValueUSD] = useState(null);
  const [loadingEndowmentValue, setLoadingEndowmentValue] = useState(true);
  const [endowmentError, setEndowmentError] = useState(null);

  const endowmentTotals = useMemo(
    () => buildEndowmentTotals(ociPoolSnapshot),
    [ociPoolSnapshot]
  );

  useEffect(() => {
    document.body.style.overflow = 'auto';

    let isMounted = true;

    const fetchEndowmentValue = async () => {
      setLoadingEndowmentValue(true);
      setEndowmentError(null);

      let liveOciSnapshot = OCI_ENDOWMENT_FALLBACK_SNAPSHOT;

      try {
        liveOciSnapshot = await fetchOciEndowmentPoolSnapshot();
      } catch (ociError) {
        console.warn('Failed to fetch live Oci endowment snapshot:', ociError);
      }

      if (isMounted) {
        setOciPoolSnapshot(liveOciSnapshot);
      }

      try {
        const response = await fetch(ASTROLESCENT_API_URL);
        if (!response.ok) {
          throw new Error(`Status: ${response.status}`);
        }

        const priceMap = await response.json();

        if (isMounted) {
          setEndowmentValueUSD(
            calculateEndowmentTotalValueUSD(priceMap, liveOciSnapshot)
          );
        }
      } catch (error) {
        console.error('Error fetching endowment value:', error);
        if (isMounted) {
          setEndowmentError(error.message || 'Failed to calculate endowment value.');
        }
      } finally {
        if (isMounted) {
          setLoadingEndowmentValue(false);
        }
      }
    };

    fetchEndowmentValue();

    return () => {
      isMounted = false;
      document.body.style.overflow = '';
    };
  }, []);

  const completedDonations = charityDonationsData.filter(
    (donation) => donation.status.toLowerCase() === 'done'
  );
  const totalDonated = completedDonations.reduce(
    (sum, donation) => sum + donation.amount,
    0
  );

  return (
    <div className="charity-page" data-scroll-root>
      <Header />

      <main className="charity-content">
        <section className="charity-hero-section">
          <div className="container">
            <h1>Reddicks Cares</h1>
            <p className="subtitle">
              The endowment now consists of the permanently locked Oci
              REDDICKS/XRD pool plus the separate 1.2M DCKS reserve. Fees and
              future interest accrue on top of that principal to be donated
              yearly.
            </p>

            <div className="charity-value-cards">
              <div className="charity-value-card primary">
                <div className="card-icon">
                  <FaHeart />
                </div>
                <div className="card-content">
                  <h4>Current Endowment Fund</h4>
                  <div className="charity-amount">
                    {loadingEndowmentValue
                      ? 'Loading...'
                      : endowmentError
                        ? 'Unavailable'
                        : formatCurrency(endowmentValueUSD)}
                  </div>
                  <p className="data-note">
                    Live Oci principal plus the separate 1.2M DCKS reserve.
                  </p>
                </div>
              </div>

              <div className="charity-value-card secondary">
                <div className="card-icon">
                  <FaGift />
                </div>
                <div className="card-content">
                  <h4>Direct Donations</h4>
                  <div className="charity-amount">{formatCurrency(totalDonated)}</div>
                  <p className="data-note">
                    {completedDonations.length} confirmed direct donations logged
                    so far.
                  </p>
                </div>
              </div>

              <div className="charity-value-card tertiary">
                <div className="card-icon">
                  <FaShieldAlt />
                </div>
                <div className="card-content">
                  <h4>Locked Oci Principal</h4>
                  <div className="charity-amount">
                    {formatCurrency(endowmentTotals.OCI_POOL_USD)}
                  </div>
                  <p className="data-note">
                    Currently about {formatTokenAmount(endowmentTotals.OCI_POOL_DCKS)} DCKS and{' '}
                    {formatTokenAmount(endowmentTotals.OCI_POOL_XRD)} XRD earning fees for
                    the endowment.
                  </p>
                </div>
              </div>
            </div>

            <p className="charity-data-source">
              Live endowment figures reference Ociswap pool data and the
              Astrolescent price API.
            </p>
          </div>
        </section>

        <section className="charity-about-section">
          <div className="container">
            <h2 className="section-title">How The Endowment Works</h2>
            <div className="endowment-flow">
              <div className="endowment-flow__step">
                <span>01</span>
                <p>Locked Pool + Seed</p>
              </div>
              <div className="endowment-flow__arrow">-&gt;</div>
              <div className="endowment-flow__step">
                <span>02</span>
                <p>Principal Grows</p>
              </div>
              <div className="endowment-flow__arrow">-&gt;</div>
              <div className="endowment-flow__step">
                <span>03</span>
                <p>Annual Yield Donated</p>
              </div>
              <div className="endowment-flow__arrow">-&gt;</div>
              <div className="endowment-flow__step">
                <span>04</span>
                <p>DAO Direction</p>
              </div>
            </div>
            <div className="charity-grid">
              <div className="charity-card">
                <FaHandHoldingHeart className="charity-icon" />
                <h3>Locked Principal</h3>
                <p>
                  The full Oci REDDICKS/XRD pool is now being treated as
                  endowment principal rather than treasury inventory.
                </p>
              </div>

              <div className="charity-card">
                <FaShieldAlt className="charity-icon" />
                <h3>Seed Reserve</h3>
                <p>
                  A separate 1.2M DCKS handover now sits beside the locked Oci
                  position as additional endowment principal.
                </p>
              </div>

              <div className="charity-card">
                <FaCalendarAlt className="charity-icon" />
                <h3>Yearly Fee Donations</h3>
                <p>
                  The annual donation target is the generated fees and interest,
                  not the principal, keeping the fund durable over time.
                </p>
              </div>

              <div className="charity-card">
                <FaUsers className="charity-icon" />
                <h3>DAO Direction</h3>
                <p>
                  Donation destinations are expected to follow DAO direction as
                  governance becomes more active.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="donation-history-section">
          <div className="container">
            <h2 className="section-title">Donation History</h2>
            <div className="donations-table-container">
              <div className="table-header">
                <h3>Direct Donations Logged So Far</h3>
                <p>
                  Direct contributions are listed here. The locked Oci principal
                  and the 1.2M DCKS reserve are tracked separately above because
                  they form the endowment fund, not spent donations.
                </p>
              </div>

              <div className="donations-table">
                <div className="table-row header-row">
                  <div className="table-cell">Date</div>
                  <div className="table-cell">Organization</div>
                  <div className="table-cell">Amount</div>
                  <div className="table-cell">Focus Area</div>
                  <div className="table-cell">Status</div>
                  <div className="table-cell">Reference</div>
                </div>

                {charityDonationsData.map((donation, index) => (
                  <div key={index} className="table-row data-row">
                    <div className="table-cell date-cell">
                      {new Date(donation.date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </div>
                    <div className="table-cell org-cell">
                      <div className="org-info">
                        <h4>{donation.organization}</h4>
                        <p>{donation.description}</p>
                      </div>
                    </div>
                    <div className="table-cell amount-cell">
                      {formatCurrency(donation.amount)}
                    </div>
                    <div className="table-cell focus-cell">
                      <span className="focus-tag">{donation.focus}</span>
                    </div>
                    <div className="table-cell status-cell">
                      <span className="status-badge done">{donation.status}</span>
                    </div>
                    <div className="table-cell proof-cell">
                      <span className="proof-link">{donation.referenceText}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="charity-impact-section">
          <div className="container">
            <h2 className="section-title">Sustainable Impact Direction</h2>
            <div className="impact-content">
              <div className="impact-text">
                <p>
                  REDDICKS charity is being treated as long-term infrastructure.
                  The endowment is now separated cleanly from the treasury so
                  the principal can be tracked on its own.
                </p>
                <p>
                  The permanently locked Oci REDDICKS/XRD pool plus the
                  additional 1.2M DCKS reserve now form the current endowment
                  fund base.
                </p>
                <p>
                  The most recent direct contribution was another $540 to Xian
                  Development on April 2, 2026, while the endowment structure
                  was being separated out.
                </p>
                <p>
                  Fees and future interest are intended to be collected and
                  donated yearly, while the principal itself remains intact.
                </p>
              </div>

              <div className="next-donation-card">
                <div className="card-header">
                  <FaCalendarAlt className="header-icon" />
                  <h3>Current Endowment</h3>
                </div>
                <div className="card-body">
                  <p className="cycle-info">
                    The endowment is now tracked separately from the treasury.
                    The locked Oci pool and the additional DCKS reserve are the
                    current principal, while fees and future interest are
                    intended to be donated yearly.
                  </p>
                  <div className="current-fund">
                    <span className="fund-label">Total Endowment:</span>
                    <span className="fund-amount">
                      {loadingEndowmentValue
                        ? 'Loading...'
                        : endowmentError
                          ? 'Unavailable'
                          : formatCurrency(endowmentValueUSD)}
                    </span>
                  </div>
                  <div className="current-fund">
                    <span className="fund-label">Locked Oci Pool:</span>
                    <span className="fund-amount">
                      {formatCurrency(endowmentTotals.OCI_POOL_USD)}
                    </span>
                  </div>
                  <div className="current-fund">
                    <span className="fund-label">Seed Reserve:</span>
                    <span className="fund-amount">
                      {formatTokenAmount(ENDOWMENT_SEED_DCKS)} DCKS
                    </span>
                  </div>
                  <p className="fund-note">
                    Live composition: about {formatTokenAmount(endowmentTotals.OCI_POOL_DCKS)}{' '}
                    DCKS and {formatTokenAmount(endowmentTotals.OCI_POOL_XRD)} XRD in
                    the permanently locked Oci pool, plus the separate 1.2M
                    DCKS reserve.
                  </p>
                  <a
                    className="fund-link"
                    href={ociPoolSnapshot.poolUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Oci pool <FaArrowRight />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="charity-base-cta-section">
          <div className="container">
            <div className="charity-base-cta-card">
              <h2 className="section-title">For Base Users</h2>
              <p>
                Finished reading the charity model. Re-enter through the live
                Base market surface below.
              </p>
              <div className="charity-base-cta-actions">
                <a href={BASE_BUY_URL} target="_blank" rel="noopener noreferrer">
                  Buy DCKS on Base <FaArrowRight />
                </a>
                <a href={BASE_CHART_URL} target="_blank" rel="noopener noreferrer">
                  View Base Chart <FaArrowRight />
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <TelegramPopup />
    </div>
  );
};

export default Charity;
